import React from 'react';
import { Box, Typography, Chip, Avatar, ButtonBase, Tooltip, IconButton } from '@mui/material';
import {
  Warning as AlertIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Medication as MedIcon,
  AccessTime as TimeIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import HoofIcon from '../../components/HoofIcon';
import HorsePhotoAvatar from '../../components/HorsePhotoAvatar';
import { Horse } from '../../utils/api';
import { formatBreed } from '../../utils/breed';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

export interface PatientCardProps {
  horse: Horse;
  onOpen: () => void;
  /**
   * OPTIONAL: omitted means the viewer may not edit patients, and the pencil is not rendered
   * (INV-108 — hide, never disable). The gate itself lives on the page that owns the control map,
   * so this stays a presentational component with no permission knowledge of its own.
   */
  onEdit?: () => void;
}

const statusMeta = (status?: string) => {
  if (status === 'INACTIVE') return { label: 'Inactive', color: 'default' as const, dot: '#9E9E9E' };
  if (status === 'DECEASED') return { label: 'Deceased', color: 'error' as const, dot: '#C62828' };
  return { label: 'Active', color: 'success' as const, dot: '#2E7D32' };
};

/**
 * Compact tile for the grid view of the patient list. Surfaces the same
 * facts the list row shows — name + breed + owner — plus visual flags
 * (status dot, critical alert, contact icons) that are easier to scan in a
 * tile layout. Click anywhere on the body opens the chart; the small Edit
 * affordance opens the horse edit dialog without navigating away.
 */
const PatientCard: React.FC<PatientCardProps> = ({ horse, onOpen, onEdit }) => {
  const status = statusMeta(horse.status);
  const formatInstant = useFormatInstant();
  const criticalAlerts = Array.isArray(horse.medicalAlerts)
    ? (horse.medicalAlerts as any[]).filter((a) => typeof a === 'object' && a?.isCritical)
    : [];
  const owner = horse.owners?.[0];

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: criticalAlerts.length > 0 ? 'rgba(244, 67, 54, 0.4)' : 'divider',
        bgcolor: '#fff',
        transition: 'all 140ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 4px 14px rgba(0,0,0,0.07)',
          transform: 'translateY(-1px)',
          borderColor: criticalAlerts.length > 0 ? 'rgba(244, 67, 54, 0.6)' : '#C9772B',
        },
      }}
      data-testid={`patient-card-${horse.id}`}
    >
      <ButtonBase
        onClick={onOpen}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          p: 1.5,
          textAlign: 'left',
          borderRadius: 2.5,
          minHeight: 168,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          <HorsePhotoAvatar horseId={horse.id} photoUrl={horse.photoUrl} size={40} sx={{ flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {horse.name}
              </Typography>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: status.dot,
                  flexShrink: 0,
                }}
              />
            </Box>
            {horse.patientId && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                {horse.patientId}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.78rem',
            mb: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {[formatBreed(horse), horse.gender].filter(Boolean).join(' · ') || '—'}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: 'text.primary',
            fontSize: '0.78rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {owner ? owner.name : 'No owner'}
        </Typography>

        {/* Barn / stabling location. Lets vets group "who's at which farm"
            at a glance — useful for barn-call routing. */}
        {horse.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25, minWidth: 0 }}>
            <PlaceIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {horse.location}
            </Typography>
          </Box>
        )}

        {/* Flag row: critical alerts + active Rx count + status. Enriched
            via the backend's paginated horses response so the tile carries
            the same at-a-glance signals as the list row. */}
        <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minHeight: 24 }}>
          {criticalAlerts.length > 0 && (
            <Tooltip title={criticalAlerts.map((a: any) => a.text).join(' · ')} arrow>
              <Chip
                icon={<AlertIcon sx={{ fontSize: 12 }} />}
                label={criticalAlerts.length === 1 ? criticalAlerts[0].text : `${criticalAlerts.length} alerts`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  maxWidth: '100%',
                  bgcolor: '#E65100',
                  color: '#fff',
                  '& .MuiChip-icon': { ml: 0.5, color: '#fff' },
                  '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </Tooltip>
          )}
          {(horse.activeMedicationCount ?? 0) > 0 && (
            <Tooltip title={`${horse.activeMedicationCount} active medication${horse.activeMedicationCount === 1 ? '' : 's'}`} arrow>
              <Chip
                icon={<MedIcon sx={{ fontSize: 12 }} />}
                label={`Rx ${horse.activeMedicationCount}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(46,125,50,0.12)',
                  color: '#2E7D32',
                  border: '1px solid rgba(46,125,50,0.3)',
                  '& .MuiChip-icon': { ml: 0.5, color: '#2E7D32' },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Tooltip>
          )}
          {horse.status === 'INACTIVE' && (
            <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          {horse.status === 'DECEASED' && (
            <Chip size="small" label="Deceased" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#7F1D1D', color: '#fff', borderRadius: '999px', '& .MuiChip-label': { px: 1 } }} />
          )}
          {horse.lastVisitAt && (
            /* INSTANT — `lastVisitAt` is not a column: horses.service.ts derives it as
               MAX(Appointment.date) per horse, and Appointment.date is a real moment
               (DATE-KINDS §1.3). So its DAY is the clinic's day of that visit, which
               only the clinic zone can name — the browser's `toLocaleDateString()`
               printed the viewer's day instead. */
            <Tooltip
              title={`Last visit: ${formatInstant(horse.lastVisitAt, INSTANT_FORMATS.dateNumeric)}`}
              arrow
            >
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <TimeIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {/* Zone-agnostic: formatDistanceToNow diffs two instants, so no zone
                      can change "3 days ago". Left as is on purpose (DATE-KINDS §2.3
                      carve-out); the ratchet still counts the spelling. */}
                  {formatDistanceToNow(new Date(horse.lastVisitAt), { addSuffix: true })}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </ButtonBase>

      {/* Hover-only quick actions on the top-right */}
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          display: 'flex',
          gap: 0.25,
          opacity: 0,
          transition: 'opacity 120ms',
          '.MuiBox-root:hover > &': { opacity: 1 },
          [`[data-testid="patient-card-${horse.id}"]:hover &`]: { opacity: 1 },
        }}
      >
        {owner?.email && (
          <Tooltip title={`Email ${owner.name}`} arrow>
            <IconButton
              size="small"
              component="a"
              href={`mailto:${owner.email}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0.5, bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Edit patient" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              sx={{ p: 0.5, bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default PatientCard;
